// src/components/topbar/UserMenu.js
import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { Role } from '../../utils/enums';
import './UserMenu.css'; // Styles for UserMenu are defined here.
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { IS_PRERENDERING } from '../../utils/config';

const UserMenu = () => {
  const { user, updateUser, updateToken } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLanguage();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [isAdminSubMenuOpen, setIsAdminSubMenuOpen] = useState(false);
  const [isSellerSubMenuOpen, setIsSellerSubMenuOpen] = useState(false);
  const [isLegalSubMenuOpen, setIsLegalSubMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const adminMenuTimerRef = useRef(null);
  const sellerMenuTimerRef = useRef(null);
  const legalMenuTimerRef = useRef(null);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // State to track the last used input method ('mouse' or 'touch')
  const [lastInputMethod, setLastInputMethod] = useState('mouse');

  useEffect(() => {
    const handleTouchStart = () => {
      if (lastInputMethod !== 'touch') setLastInputMethod('touch');
    };
    const handleMouseMove = () => {
      if (lastInputMethod !== 'mouse') setLastInputMethod('mouse');
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [lastInputMethod]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsAdminSubMenuOpen(false);
    setIsSellerSubMenuOpen(false);
    setIsLegalSubMenuOpen(false);
    clearTimeout(adminMenuTimerRef.current);
    clearTimeout(sellerMenuTimerRef.current);
    clearTimeout(legalMenuTimerRef.current);
  };

  // Toggle functions for click-to-open submenus
  const toggleAdminSubMenu = (e) => {
    e.stopPropagation(); // Prevent closing the main menu on click
    setIsAdminSubMenuOpen((prev) => !prev);
    setIsSellerSubMenuOpen(false); // Close other submenus
    setIsLegalSubMenuOpen(false);
  };

  const toggleSellerSubMenu = (e) => {
    e.stopPropagation();
    setIsSellerSubMenuOpen((prev) => !prev);
    setIsAdminSubMenuOpen(false);
    setIsLegalSubMenuOpen(false);
  };

  const toggleLegalSubMenu = (e) => {
    e.stopPropagation();
    setIsLegalSubMenuOpen((prev) => !prev);
    setIsAdminSubMenuOpen(false);
    setIsSellerSubMenuOpen(false);
  };

  // Hover handlers for desktop
  const handleAdminMouseEnter = () => {
    if (lastInputMethod === 'touch') return;
    clearTimeout(adminMenuTimerRef.current);
    if (isMenuOpen) {
      setIsAdminSubMenuOpen(true);
    }
  };

  const handleAdminMouseLeave = () => {
    if (lastInputMethod === 'touch') return;
    adminMenuTimerRef.current = setTimeout(() => {
      setIsAdminSubMenuOpen(false);
    }, 200);
  };

  const handleSellerMouseEnter = () => {
    if (lastInputMethod === 'touch') return;
    clearTimeout(sellerMenuTimerRef.current);
    if (isMenuOpen) {
      setIsSellerSubMenuOpen(true);
    }
  };

  const handleSellerMouseLeave = () => {
    if (lastInputMethod === 'touch') return;
    sellerMenuTimerRef.current = setTimeout(() => {
      setIsSellerSubMenuOpen(false);
    }, 200);
  };

  const handleLegalMouseEnter = () => {
    if (lastInputMethod === 'touch') return;
    clearTimeout(legalMenuTimerRef.current);
    if (isMenuOpen) {
      setIsLegalSubMenuOpen(true);
    }
  };

  const handleLegalMouseLeave = () => {
    if (lastInputMethod === 'touch') return;
    legalMenuTimerRef.current = setTimeout(() => {
      setIsLegalSubMenuOpen(false);
    }, 200);
  };

  const handleSignOut = () => {
    updateUser({
      isLoggedIn: false,
      fullName: '',
      username: '',
      role: Role.Guest,
      itemsPerPage: 12, // Default value
      subscribe: false, // Default to not subscribed on logout
      creditPoints: 0,
      savedProperties: [],
    });
    updateToken(null);
    closeMenu();
    navigate(`/${lang}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(adminMenuTimerRef.current);
      clearTimeout(sellerMenuTimerRef.current);
      clearTimeout(legalMenuTimerRef.current);
    };
  }, [isMenuOpen]);

  return (
    <div className="menu-container" ref={menuRef}>
      {user.isLoggedIn && !IS_PRERENDERING && (
        <div className="user-info-cluster">
          <span className="user-name-display">
            {user.fullName.split(' ')[0]}
            {user.role === Role.Admin && (
              <>
                <br />
                {t('userMenu.roleAdmin')}
              </>
            )}
            {user.role === Role.Seller && (
              <>
                <br />
                {t('userMenu.roleSeller')}
              </>
            )}
            {user.role === Role.User && (
              <>
                <br />
                {t('userMenu.roleVerifiedUser')}
              </>
            )}
          </span>
        </div>
      )}
      <button className={`menu-button hamburger-button ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label={t('userMenu.ariaLabel')} aria-expanded={isMenuOpen}>
        <span className="hamburger-box">
          <span className="hamburger-inner"></span>
        </span>
      </button>

      {isMenuOpen && (
        <ul className="menu-list" style={{ visibility: IS_PRERENDERING && user.isLoggedIn ? 'hidden' : 'visible' }}>
          <li>
            <Link to={`/${lang}`} onClick={closeMenu}>
              {t('userMenu.home')}
            </Link>
          </li>
          {!user.isLoggedIn && (
            <>
              <li className="menu-separator"></li>
              <li>
                <Link to={`/${lang}/login`} onClick={closeMenu}>
                  {t('userMenu.signIn')}
                </Link>
              </li>
            </>
          )}
          {user.isLoggedIn && !IS_PRERENDERING && (
            <>
              <li className="menu-separator"></li>
              <li>
                <Link to={`/${lang}/preferences`} onClick={closeMenu}>
                  {t('userMenu.editPreferences')}
                </Link>
              </li>
              <li>
                <Link to={`/${lang}/map?show=saved`} onClick={closeMenu}>
                  {t('userMenu.mySavedHomes', { count: user.savedProperties?.length || 0 })}
                </Link>
              </li>
              <li>
                <Link to={`/${lang}/my-orders`} onClick={closeMenu}>
                  {t('userMenu.myOrders')}
                </Link>
              </li>
              {user.isSeller && (
                <li className={`admin-tools-item ${isSellerSubMenuOpen ? 'submenu-open' : ''}`} onMouseEnter={handleSellerMouseEnter} onMouseLeave={handleSellerMouseLeave}>
                  <span className="admin-tools-trigger" onClick={toggleSellerSubMenu}>
                    <span className="submenu-arrow">▸</span> {t('userMenu.sellerTools')}
                  </span>
                  {isSellerSubMenuOpen && (
                    <ul className="admin-submenu" onMouseEnter={handleSellerMouseEnter} onMouseLeave={handleSellerMouseLeave}>
                      <li>
                        <Link to={user.isAdmin ? `/${lang}/properties?view=my&scope=self` : `/${lang}/properties?view=my`} onClick={closeMenu}>
                          {t('userMenu.myProperties')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/statistics`} onClick={closeMenu}>
                          {t('userMenu.statistics')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/inquiries`} onClick={closeMenu}>
                          {t('userMenu.myInquiries')}
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
              )}
              {user.isAdmin && (
                <li className={`admin-tools-item ${isAdminSubMenuOpen ? 'submenu-open' : ''}`} onMouseEnter={handleAdminMouseEnter} onMouseLeave={handleAdminMouseLeave}>
                  <span className="admin-tools-trigger" onClick={toggleAdminSubMenu}>
                    <span className="submenu-arrow">▸</span> {t('userMenu.adminTools')}
                  </span>
                  {isAdminSubMenuOpen && (
                    <ul className="admin-submenu" onMouseEnter={handleAdminMouseEnter} onMouseLeave={handleAdminMouseLeave}>
                      <li>
                        <Link to={`/${lang}/properties?view=my`} onClick={closeMenu}>
                          {t('userMenu.properties')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/my-orders?view=all`} onClick={closeMenu}>
                          {t('userMenu.orders')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/inquiries?view=all`} onClick={closeMenu}>
                          {t('userMenu.inquiries')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/admin/messages`} onClick={closeMenu}>
                          {t('userMenu.messages')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/admin/user-management`} onClick={closeMenu}>
                          {t('userMenu.userManagement')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/admin/admin-emails`} onClick={closeMenu}>
                          {t('userMenu.adminEmails')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/admin/announcement`} onClick={closeMenu}>
                          {t('userMenu.announcement')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/admin/events`} onClick={closeMenu}>
                          {t('userMenu.eventViewer')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/admin/tasks`} onClick={closeMenu}>
                          {t('userMenu.taskRunner')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/admin/log-viewer`} onClick={closeMenu}>
                          {t('userMenu.logViewer')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/testGoogleAddress`} onClick={closeMenu}>
                          {t('userMenu.testGoogleAddress')}
                        </Link>
                      </li>
                      <li>
                        <Link to={`/${lang}/testOpenStreetMapAddress`} onClick={closeMenu}>
                          {t('userMenu.testOpenStreetMap')}
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
              )}
              <li>
                <button className="menu-signout-button" onClick={handleSignOut}>
                  {t('userMenu.signOut')}
                </button>
              </li>
            </>
          )}
          <li className="menu-separator"></li>
          <li>
            <Link to={`/${lang}/about`} onClick={closeMenu}>
              {t('userMenu.about')}
            </Link>
          </li>
          <li>
            <Link to={`/${lang}/contact-us`} onClick={closeMenu}>
              {t('userMenu.contactUs')}
            </Link>
          </li>
          <li className={`admin-tools-item ${isLegalSubMenuOpen ? 'submenu-open' : ''}`} onMouseEnter={handleLegalMouseEnter} onMouseLeave={handleLegalMouseLeave}>
            <span className="admin-tools-trigger" onClick={toggleLegalSubMenu}>
              <span className="submenu-arrow">▸</span> {t('userMenu.legal')}
            </span>
            {isLegalSubMenuOpen && (
              <ul className="admin-submenu" onMouseEnter={handleLegalMouseEnter} onMouseLeave={handleLegalMouseLeave}>
                {user.isSeller && (
                  <li>
                    <Link to={`/${lang}/provider-terms-of-use`} onClick={closeMenu}>
                      {t('userMenu.sellerTermsOfUse')}
                    </Link>
                  </li>
                )}
                <li>
                  <Link to={`/${lang}/terms-of-use`} onClick={closeMenu}>
                    {t('userMenu.termsOfUse')}
                  </Link>
                </li>
                <li>
                  <Link to={`/${lang}/privacy-policy`} onClick={closeMenu}>
                    {t('userMenu.privacyPolicy')}
                  </Link>
                </li>
                <li>
                  <Link to={`/${lang}/cancellation-policy`} onClick={closeMenu}>
                    {t('userMenu.cancellationPolicy')}
                  </Link>
                </li>
                <li>
                  <Link to={`/${lang}/cookie-policy`} onClick={closeMenu}>
                    {t('userMenu.cookiePolicy')}
                  </Link>
                </li>
              </ul>
            )}
          </li>
        </ul>
      )}
    </div>
  );
};

export default UserMenu;
